import { format, isValid, parseISO, type Locale } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import { logger } from './logger';

/** date-fns localized medium date (e.g. en-US "Aug 10, 2026", fr-FR "10 août 2026"). */
const DEFAULT_DATE_PATTERN = 'PP';
// "PPpp" is the format for date-fns locales. PP is the (longer) date format,
// pp is the (longer)time format. Combined, it is the (longer) date and time format.
// "MM/DD/YYYY h:mm A" is the format for US locales.
const DEFAULT_DATETIME_PATTERN = 'PPpp';
// String returned when the helper can't produce a valid formatted date or time.
const FALLBACK_TEXT = 'Invalid date';
const DEFAULT_DATE_FNS_LOCALE = dateFnsLocales.enUS as Locale;

const normalizeLocaleCandidates = (locale?: string): string[] => {
  if (!locale) {
    return [];
  }

  const trimmed = locale.trim();
  if (!trimmed) {
    return [];
  }

  const withoutHyphen = trimmed.replace(/-/g, '');
  const [languagePart] = trimmed.split('-');
  const capitalizedRegion = trimmed
    .split('-')
    .map((part, index) => (index === 0 ? part : part.toUpperCase()))
    .join('');

  return [trimmed, withoutHyphen, languagePart, capitalizedRegion].filter(
    Boolean
  ) as string[];
};

const resolveDateFnsLocale = (locale?: string): Locale => {
  const candidates = normalizeLocaleCandidates(locale);

  for (const candidate of candidates) {
    if (candidate in dateFnsLocales) {
      return dateFnsLocales[candidate as keyof typeof dateFnsLocales] as Locale;
    }
  }

  return DEFAULT_DATE_FNS_LOCALE;
};

const convertToDate = (
  value: Date | string | number | null | undefined
): Date | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return isValid(value) ? value : null;
  }

  if (typeof value === 'number') {
    const dateFromNumber = new Date(value);
    return isValid(dateFromNumber) ? dateFromNumber : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsedIso = parseISO(trimmed);
    if (isValid(parsedIso)) {
      return parsedIso;
    }

    const parsedDate = new Date(trimmed);
    return isValid(parsedDate) ? parsedDate : null;
  }

  return null;
};

interface FormatOptions {
  pattern?: string;
  fallbackText?: string;
}

interface LocaleDateFormatter {
  formatDate: (
    value: Date | string | number | null | undefined,
    options?: FormatOptions
  ) => string;
  formatDateTime: (
    value: Date | string | number | null | undefined,
    options?: FormatOptions
  ) => string;
  toDate: (value: Date | string | number | null | undefined) => Date | null;
  locale: Locale;
  localeCode?: string;
}

const formatValue = (
  value: Date | string | number | null | undefined,
  pattern: string,
  fallbackText: string,
  locale: Locale
): string => {
  const date = convertToDate(value);
  if (!date) {
    return fallbackText;
  }

  try {
    return format(date, pattern, { locale });
  } catch (err) {
    logger.warn('Date format failed, using fallback:', value, pattern, err);
    return fallbackText;
  }
};

export const createLocaleDateFormatter = (
  localeCode?: string
): LocaleDateFormatter => {
  const dateFnsLocale = resolveDateFnsLocale(localeCode);

  return {
    formatDate: (value, options) =>
      formatValue(
        value,
        options?.pattern || DEFAULT_DATE_PATTERN,
        options?.fallbackText || FALLBACK_TEXT,
        dateFnsLocale
      ),
    formatDateTime: (value, options) =>
      formatValue(
        value,
        options?.pattern || DEFAULT_DATETIME_PATTERN,
        options?.fallbackText || FALLBACK_TEXT,
        dateFnsLocale
      ),
    toDate: value => convertToDate(value),
    locale: dateFnsLocale,
    localeCode,
  };
};

/**
 * Parses a raw date value (ISO string, Date, epoch ms, null/undefined) into a
 * `Date` object, or `null` when the value is absent or unparseable.
 *
 * Named `parseDateValue` rather than `formatDateForLocale` because the function
 * returns a Date — it does not format anything — and the optional `localeCode`
 * argument only influences which Day.js locale plugin is loaded for parsing.
 */
export const parseDateValue = (
  value: Date | string | number | null | undefined,
  localeCode?: string
): Date | null => {
  const formatter = createLocaleDateFormatter(localeCode);
  return formatter.toDate(value);
};

export type { LocaleDateFormatter, FormatOptions };
